//@flow
import React, { useState, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import get from 'lodash.get';

import { Spinner } from 'dibs-elements/exports/Spinner';

import type { DisplayPromoCode_user } from './__generated__/DisplayPromoCode_user.graphql'; // eslint-disable-line camelcase
import type { DisplayPromoCode_promotion } from './__generated__/DisplayPromoCode_promotion.graphql'; // eslint-disable-line camelcase
import type { DisplayPromoCode_item } from './__generated__/DisplayPromoCode_item.graphql'; // eslint-disable-line camelcase

// TRADE-3810 requires some one-off logic for this promo because it guarantees free returns, thus there is no amount, so the usual display logic doesn't work
const RISKFREE_PROMO_CODE = 'RISKFREE';

type currencyType = {
    amount: ?number,
    currency: ?string,
};

const SHIPPING_PROMO_REFERENCE = 'FIRSTDIBS_PROMOTION_SHIPPING_DISCOUNT';

function getUserCurrencyAmount(financialAmount, userCurrency: ?string): currencyType {
    if (!financialAmount) {
        return {
            amount: null,
            currency: null,
        };
    }

    let amount = financialAmount.amount;
    let currency = financialAmount.currency;

    if (financialAmount.currency === userCurrency) {
        return { amount, currency };
    }

    const convertedAmountList = financialAmount.convertedAmountList;
    if (convertedAmountList && Array.isArray(convertedAmountList)) {
        const convertedAmount = convertedAmountList.find(
            _amount => (_amount || {}).currency === userCurrency
        );

        if (convertedAmount) {
            amount = convertedAmount.amount;
            currency = convertedAmount.currency;
        }
    }

    return { amount, currency };
}

type DisplayPromoCodeProps = {
    user: ?DisplayPromoCode_user, // eslint-disable-line camelcase
    item: ?DisplayPromoCode_item, // eslint-disable-line camelcase
    promotion: ?DisplayPromoCode_promotion, // eslint-disable-line camelcase
    isFurtherReduced?: boolean,
    displaySingleLine?: boolean,
    containerClass?: string,
    promoDisplayClass?: string,
    seeDetailsClass?: string,
    hideDetails?: boolean,
    onPromoCodeRedeem?: Function,
    hasPromo?: boolean,
    showDetails?: boolean,
};

const DisplayPromoCode = (props: DisplayPromoCodeProps) => {
    const { user, promotion, item, onPromoCodeRedeem, showDetails = true, hasPromo } = props;
    const [promoCodeApplied, setPromoCodeApplied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setIsLoading(false);
    }, [hasPromo]);

    if (get(item, 'isSold') || get(item, 'isOnHoldForDifferentBuyer')) {
        return null;
    }

    if (!promotion) {
        return null;
    }

    let discount;

    const code = get(promotion, 'code');
    const isRiskFreePromo = code === RISKFREE_PROMO_CODE;

    if (!code) {
        return null;
    }

    const percentageAmount = get(promotion, 'percentAdjustmentRate.rate');

    const userCurrency = get(user, 'profile.currency');

    if (percentageAmount) {
        discount = (
            <FormattedNumber
                style="percent"
                value={percentageAmount / 100}
                id="common.DisplayPromoCode.percentage"
            />
        );
    } else {
        const rate = get(promotion, 'flatAdjustmentRate.rate');
        const { amount, currency } = getUserCurrencyAmount(rate, userCurrency);

        if (amount && currency) {
            discount = <FormattedNumber value={amount} style="currency" currency={currency} />;
        }
    }

    // the RISKFREE promo does not have a discount, see TRADE-3810
    if (!discount && !isRiskFreePromo) {
        return null;
    }

    const extraOff = props.isFurtherReduced ? (
        <FormattedMessage id="DisplayPromoCode.extraOff" defaultMessage="Extra " />
    ) : (
        ''
    );

    const promoReference = get(promotion, 'adjustmentRateList[0].reference');

    const promoType =
        promoReference === SHIPPING_PROMO_REFERENCE ? (
            <FormattedMessage id="DisplayPromoCode.shippingPromo" defaultMessage="shipping " />
        ) : (
            ''
        );
    let itemPrice = getUserCurrencyAmount(get(item, 'pricing.retailPrice'), userCurrency);
    const salePrice = getUserCurrencyAmount(get(item, 'pricing.salePrice'), userCurrency);
    const netPrice = getUserCurrencyAmount(get(item, 'pricing.netPrice'), userCurrency);

    if (netPrice.amount) {
        itemPrice = netPrice;
    } else if (salePrice.amount) {
        itemPrice = salePrice;
    }

    const priceRange = get(promotion, 'priceRange') || [];
    const minAmount = getUserCurrencyAmount(
        (priceRange.find(range => range && range.minAmount) || {}).minAmount,
        userCurrency
    );
    const maxAmount = getUserCurrencyAmount(
        (priceRange.find(range => range && range.maxAmount) || {}).maxAmount,
        userCurrency
    );

    let priceDisplay = '';
    if (
        itemPrice.amount &&
        minAmount.amount &&
        minAmount.currency &&
        minAmount.amount > itemPrice.amount
    ) {
        priceDisplay = (
            <FormattedMessage
                id="common.DisplayPromoCode.belowMinimum"
                defaultMessage={' on items above {amount}'}
                values={{
                    amount: (
                        <FormattedNumber
                            value={minAmount.amount}
                            style="currency"
                            currency={minAmount.currency}
                        />
                    ),
                }}
            />
        );
    } else if (
        itemPrice.amount &&
        maxAmount.amount &&
        maxAmount.currency &&
        maxAmount.amount < itemPrice.amount
    ) {
        priceDisplay = (
            <FormattedMessage
                id="common.DisplayPromoCode.aboveMaximum"
                defaultMessage={' on items below {amount}'}
                values={{
                    amount: (
                        <FormattedNumber
                            value={maxAmount.amount}
                            style="currency"
                            currency={maxAmount.currency}
                        />
                    ),
                }}
            />
        );
    }

    const togglePromoCode = () => {
        if (!isLoading) {
            setPromoCodeApplied(!promoCodeApplied);
            if (promoCodeApplied) {
                onPromoCodeRedeem();
            } else {
                onPromoCodeRedeem(code);
            }
        }
        setIsLoading(true);
    };

    const displayCode = isRiskFreePromo ? (
        <FormattedMessage
            id="dc.DisplayPromoCode.activePromotion.riskfree_promo"
            defaultMessage="Free returns and extended buyer protection with code {code}"
            values={{ code }}
        />
    ) : (
        <FormattedMessage
            id="common.DisplayPromoCode.activePromotion.pdp_cart"
            defaultMessage={'{extraOff}{discount} off {promoType}with code {code}{priceDisplay}'}
            values={{ extraOff, discount, code, priceDisplay, promoType }}
        />
    );

    const seeDetails = props.hideDetails ? null : (
        <a
            href={`/promotions/terms/?promo=${code}`}
            target="_blank"
            rel="noopener noreferrer"
            data-tn="promo-details-link"
        >
            <FormattedMessage
                id="common.DisplayPromoCode.seeDetails"
                defaultMessage="See Details"
            />
        </a>
    );

    const content = props.displaySingleLine ? (
        <div className={props.containerClass}>
            <span className={props.promoDisplayClass}>{displayCode}</span>{' '}
            <span className={props.seeDetailsClass}>{seeDetails}</span>
        </div>
    ) : (
        <div className={props.containerClass}>
            <div className={props.promoDisplayClass}>
                <span className={props.promoCodeClass}>{displayCode}</span>
                {onPromoCodeRedeem && (
                    <span onClick={() => togglePromoCode()} className={props.redeemPromoCodeClass}>
                        {isLoading && (
                            <Spinner dark containerClass={props.redeemPromoCodeSpinnerClass} />
                        )}
                        {hasPromo ? (
                            <FormattedMessage
                                id="common.DisplayPromoCode.redeem"
                                defaultMessage="Redeem"
                            />
                        ) : (
                            <FormattedMessage
                                id="common.DisplayPromoCode.remove"
                                defaultMessage="Remove"
                            />
                        )}
                    </span>
                )}
            </div>
            {showDetails && <div className={props.seeDetailsClass}>{seeDetails}</div>}
        </div>
    );

    return content;
};

export { DisplayPromoCode };

export default createFragmentContainer(DisplayPromoCode, {
    user: graphql`
        fragment DisplayPromoCode_user on User {
            profile {
                currency
            }
        }
    `,
    promotion: graphql`
        fragment DisplayPromoCode_promotion on Promotion {
            adjustmentRateList {
                reference
            }
            code
            flatAdjustmentRate {
                rate {
                    currency
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                eligibleAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
            }
            percentAdjustmentRate {
                rate
            }
            priceRange: ruleList {
                ... on PriceRangeRule {
                    minAmount {
                        currency
                        amount
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                    maxAmount {
                        currency
                        amount
                        convertedAmountList {
                            currency
                            amount
                        }
                    }
                }
            }
        }
    `,
    item: graphql`
        fragment DisplayPromoCode_item on Item {
            isOnHoldForDifferentBuyer(currentBuyerId: $userId) @include(if: $hasUserId)
            isSold
            serviceId
            pricing {
                retailPrice {
                    amount
                    currency
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                salePrice {
                    amount
                    currency
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                netPrice {
                    amount
                    currency
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
        }
    `,
});
